import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { SiteShell } from "@/components/site/SiteShell";
import { PageHero } from "@/components/site/PageHero";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { Input } from "@/components/ui/input";
import { Search, Clock, ArrowUpRight } from "lucide-react";
import { BLOG_POSTS, BLOG_CATEGORIES } from "@/lib/blogData";
import { ROUTE_SEO } from "@/lib/routeSeo";

export default function Blog() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const posts = useMemo(() => {
    const filtered = BLOG_POSTS.filter(p =>
      (cat === "all" || p.category === cat) &&
      (q.trim() === "" || p.title.toLowerCase().includes(q.toLowerCase()) || p.excerpt.toLowerCase().includes(q.toLowerCase()) || (p.keywords || []).some(k => k.toLowerCase().includes(q.toLowerCase())))
    );
    // Sort by publishedAt DESC so newest posts land at the top of the grid.
    return [...filtered].sort((a, b) => {
      const da = new Date(a.publishedAt || a.date || 0).getTime();
      const db = new Date(b.publishedAt || b.date || 0).getTime();
      return db - da;
    });
  }, [q, cat]);

  return (
    <SiteShell>
      <SEO
        title={ROUTE_SEO["/blog"].title}
        description={ROUTE_SEO["/blog"].meta}
        path="/blog"
        jsonLd={breadcrumbSchema([{name: "Home", path: "/"}, {name: "Blog"}])}
      />
      <PageHero kicker="Blog" title={ROUTE_SEO["/blog"].h1} subtitle="From the team - practical advice on video production, working with crews, and getting more out of every shoot." breadcrumb={[{name: "Home", path: "/"}, {name: "Blog"}]} />
      <section className="py-10 sm:py-14 border-b border-border">
        <div className="container-site flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
          <div className="relative max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="blog-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles" className="pl-9 h-11 bg-background border-border" />
          </div>
          <div data-testid="blog-category-chips" className="flex flex-wrap gap-2">
            {BLOG_CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setCat(c.id)} data-testid={`blog-cat-${c.id}`} className={`h-9 px-4 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${cat === c.id ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-transparent" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}>{c.label}</button>
            ))}
          </div>
        </div>
      </section>
      <section className="section-pad">
        <div className="container-site">
          {posts.length === 0 ? (
            <p className="text-muted-foreground">No posts match your filter.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map(p => (
                <Link key={p.slug} to={`/post/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block rounded-2xl overflow-hidden border border-border bg-card hover:bg-[hsl(225,18%,9%)] transition-colors">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                      {(p.tags || []).slice(0,2).map(t => <span key={t}>{t}</span>)}
                    </div>
                    <h3 className="mt-3 font-display text-xl sm:text-2xl uppercase tracking-[0.02em] text-foreground leading-tight">{p.title}</h3>
                    <p className="mt-3 text-sm text-muted-foreground line-clamp-3 leading-relaxed">{p.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" />{p.readMinutes} min read</span>
                      <span className="inline-flex items-center gap-1 text-foreground">Read <ArrowUpRight className="h-3.5 w-3.5" /></span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </SiteShell>
  );
}
